"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Open, Cancelled" },
  { from: "Open", to: "Closed, Draft, Cancelled" },
  { from: "Partially Used", to: "Closed only" },
  {
    from: "Used",
    to: "Nothing. Mark As is hidden. Apply and refund have already used the balance",
  },
  {
    from: "Closed",
    to: "Open, Draft — blocked if anything was applied to invoices or refunded",
  },
  {
    from: "Cancelled",
    to: "Draft, Open — except a Goods Return, which cannot be uncancelled",
  },
];

const TYPES = [
  {
    type: "Goods Return",
    result:
      "The only type that puts quantity back into the warehouse. Return cost is the invoice batch cost, not the selling price. Cancel or uncancel of this type is blocked after stock was released",
  },
  {
    type: "Goods Rejection",
    result:
      "Value adjustment only. No stock add. Can be created from a Rejection Order, which then becomes Settled",
  },
  {
    type: "Rate Difference, Quantity Shortage, Tax Adjustment, Post Sale Discount, Service Cancellation, Other",
    result: "Value only. Warehouse quantity does not move",
  },
];

const RULES = [
  {
    topic: "Ledger posts on save, including Draft",
    rule: "A credit note posts to the general ledger as soon as it is saved. Open → Partially Used → Used / Closed does not re-post. Cancelled reverses the journal",
  },
  {
    topic: "Subscription gate",
    rule: "Only creating a credit note is limited by your plan. Editing, Mark As, Apply to Invoice, refund, and deleting are not separately gated",
  },
  {
    topic: "A primary bank account is required to create",
    rule: "Same gate as Quote, Sales Order, Delivery Challan, and Invoice. Mark one under Banking → Banks with Make this primary. The bank details print on the PDF",
  },
  {
    topic: "Credit note number",
    rule: "Auto-generated with a CN prefix unless you turn that off under Credit Note Configuration on the form. That same drawer stores Terms & Conditions. Locked after save",
  },
  {
    topic: "An invoice is required",
    rule: "Pick a pending invoice for this customer, or tick Use Invoice No and type the number. You cannot save without one of those",
  },
  {
    topic: "Division, Branch, Warehouse, Customer",
    rule: "All four are required to create, plus type, date, shipping address, and at least one item. After save, location and customer stay locked",
  },
  {
    topic: "Currency",
    rule: "Taken from the linked invoice when you pick one, otherwise from the customer. Cross-currency apply to a different invoice is blocked. An exchange-rate field appears when the currency differs from the company base",
  },
  {
    topic: "Current Balance moves on create",
    rule: "Saving reduces the customer's Current Balance on this branch by the credit note total, including a Draft. Cancel or delete puts that amount back",
  },
  {
    topic: "Only Goods Return returns stock",
    rule: "That type adds warehouse quantity at the original invoice batch cost. Other types do not touch stock. This is not Inventory Config Stock Deduct Source — it is the credit note type",
  },
  {
    topic: "You can only edit a Draft",
    rule: "Open, Partially Used, Used, Closed, and Cancelled cannot be edited. You cannot lower the total below amounts already applied to invoices",
  },
  {
    topic: "Apply to Invoice is not invoice-header Apply Credit",
    rule: "Apply to Invoice on this module uses this credit note's available balance against Sent, Partially Paid, or Overdue invoices. Apply Credit on an invoice detail is opening-balance credit from the customer branch — a different action",
  },
  {
    topic: "Apply and refund only while Open or Partially Used",
    rule: "Draft, Used, and Closed cannot be applied. Refund also needs available balance greater than zero. Used cannot change status at all",
  },
  {
    topic: "Cancelled Goods Return cannot come back",
    rule: "Uncancel is blocked because stock was released on cancel. Other types can Mark As Draft or Open from Cancelled. Cancel is blocked while refunds exist — delete the refunds first",
  },
  {
    topic: "Delete",
    rule: "Hidden once any invoice application or refund exists. The API also refuses Used, Closed, and Partially Used. Draft, Open, and Cancelled with nothing applied can be deleted",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a credit note" },
      { heading: "2. Type — especially Goods Return" },
      { heading: "3. Viewing, searching, and the list" },
      { heading: "4. Editing a credit note" },
      { heading: "5. Printing and downloading" },
      { heading: "6. Changing status" },
      { heading: "7. Apply to Invoice" },
      { heading: "8. Refund" },
      { heading: "9. Deleting a credit note" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function CreditNotesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Credit Notes
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone reducing what a customer still owes — returns,
            rejections, rate or tax adjustments — then applying that credit to
            invoices or refunding it
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Credit Note</B> is a sales adjustment against an invoice. Save
            makes it <B>Draft</B> and already posts the ledger and reduces
            Current Balance. <B>Mark As Open</B> is when you can{" "}
            <B>Apply to Invoice</B> or <B>Refund</B>. That apply is this
            credit note&apos;s available balance — not opening-balance{" "}
            <B>Apply Credit</B> on an invoice header.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a credit note
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick <B>Division</B>, then <B>Branch</B>, then <B>Warehouse</B> —
            the rest stays locked until those three are set. There is only{" "}
            <B>Save</B>. That creates a <B>Draft</B>. There is no Save &amp;
            Open.
          </p>
          <Definitions
            items={[
              {
                term: "Credit Note number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Credit Note Configuration</B> on the form (prefix and
                    next number). That same drawer stores{" "}
                    <B>Terms & Conditions</B>. Default prefix is <B>CN</B>.
                    Locked after save.
                  </>
                ),
              },
              {
                term: "Customer and shipping address",
                desc: (
                  <>
                    Customer must belong to the selected branch. The address
                    has to be one of that customer&apos;s own addresses.
                  </>
                ),
              },
              {
                term: "Invoice",
                desc: (
                  <>
                    Required. The picker lists this customer&apos;s invoices
                    that still have a balance and are <B>Sent</B>,{" "}
                    <B>Partially Paid</B>, or <B>Overdue</B>. Tick{" "}
                    <B>Use Invoice No</B> to type a number instead of picking.
                  </>
                ),
              },
              {
                term: "Type",
                desc: (
                  <>
                    Required. See section 2. Only <B>Goods Return</B> puts
                    stock back.
                  </>
                ),
              },
              {
                term: "Date, items, discounts, charges, tax",
                desc: (
                  <>
                    Credit Note Date is required. Line items with quantity and
                    rate. Discount and extra charges follow the same company
                    Discounts / Additional charges settings as other sales
                    documents. GST from customer state vs company state.
                    Round-off is on the totals.
                  </>
                ),
              },
              {
                term: "Currency and exchange rate",
                desc: (
                  <>
                    From the invoice you pick, otherwise from the customer. If
                    it differs from the company base, an <B>Exchange Rate</B>{" "}
                    field appears.
                  </>
                ),
              },
              {
                term: "From a Rejection Order",
                desc: (
                  <>
                    Convert to Credit Note on a rejection. The rejection number
                    is shown locked. Saving marks that rejection{" "}
                    <B>Settled</B>.
                  </>
                ),
              },
              {
                term: "What Save already does",
                desc: (
                  <>
                    Posts the ledger, reduces Current Balance on this branch,
                    and (for Goods Return) adds warehouse quantity. The note is
                    still Draft until you Mark As Open.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Credit Note with Invoice picker, Use Invoice No, Type, and Save only." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Type — especially Goods Return
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Type is not a label. <B>Goods Return</B> is the only one that
            returns stock.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Type
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens
                  </th>
                </tr>
              </thead>
              <tbody>
                {TYPES.map((row, i) => (
                  <tr
                    key={row.type}
                    className={
                      i < TYPES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[280px]">
                      {row.type}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="note">
            Changing type on a Draft from something else to Goods Return adds
            stock; changing away from Goods Return removes that stock. After
            Open you cannot edit type.
          </Callout>
          <Screenshot caption="Screenshot: Credit Note Type set to Goods Return, with Stock Movement on the detail after save." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Viewing, searching, and the list
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The list has status tabs. There is no Export and no Duplicate.
          </p>
          <Definitions
            items={[
              {
                term: "Finding a credit note",
                desc: (
                  <>
                    Search by credit note number, reference, or customer name.
                    Filters: date, status, division, branch, customer, and
                    added by.
                  </>
                ),
              },
              {
                term: "Row actions",
                desc: (
                  <>
                    View, Edit (Draft only), Mark Credit Note As,{" "}
                    <B>Apply to Invoice</B> (Open or Partially Used),{" "}
                    <B>Refund</B> (same statuses, available balance greater
                    than zero), Delete when nothing has been applied and there
                    are no refunds.
                  </>
                ),
              },
              {
                term: "Toolbar",
                desc: (
                  <>
                    Preferences is Credit Note Configuration (prefix, next
                    number, Terms & Conditions). Also Manage Custom Fields,
                    Refresh List, Reset Columns Width.
                  </>
                ),
              },
              {
                term: "Detail header",
                desc: (
                  <>
                    Number and status. <B>View Customer</B>, <B>Download</B>,{" "}
                    <B>Print</B>, Apply to Invoice, Refund, Edit, Delete.
                  </>
                ),
              },
              {
                term: "Detail tabs",
                desc: (
                  <>
                    Basic Info, <B>Stock Movement</B>, Credit Applied (when
                    invoices were mapped), Refund History (when refunds exist),
                    Attachments if files were uploaded, History.
                  </>
                ),
              },
              {
                term: "Unused credit on the customer list",
                desc: (
                  <>
                    That figure is available balance across this
                    customer&apos;s credit notes. It is not negative Opening
                    Balance and not Unused Amount on a Payment Received.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Credit Notes list with status tabs, Apply to Invoice, and Refund on the row menu." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing a credit note
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only a <B>Draft</B> can be edited. The API rejects any other
            status.
          </p>
          <Definitions
            items={[
              {
                term: "What stays locked even on a Draft",
                desc: (
                  <>
                    Division, Branch, Warehouse, Customer, and the credit note
                    number.
                  </>
                ),
              },
              {
                term: "What you can still change",
                desc: (
                  <>
                    Invoice, type, date, items, amounts, notes, terms, address,
                    attachments, and exchange rate. The new total cannot be
                    less than amounts already applied to invoices.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Edit disabled on an Open credit note with the status tooltip." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Print and Download on the detail header. The PDF uses the company
            primary bank, billing and shipping addresses, and authorized
            signature.
          </p>
          <Screenshot caption="Screenshot: Print and Download on a credit note detail header." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>Mark Credit Note As</B> is on the list row and the detail.
            Cancelled asks for confirm. Partially Used and Used are set when
            you apply or refund — you do not Mark As those.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    You can Mark As
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((row, i) => (
                  <tr
                    key={row.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[180px]">
                      {row.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {row.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Definitions
            items={[
              {
                term: "Open",
                desc: (
                  <>
                    Issued. Apply to Invoice and Refund become available.
                    Available balance starts as the credit note total.
                  </>
                ),
              },
              {
                term: "Partially Used / Used",
                desc: (
                  <>
                    Set automatically when you apply or refund. Used means
                    available balance is zero. Used cannot change status.
                  </>
                ),
              },
              {
                term: "Closed",
                desc: (
                  <>
                    Manual stop from Open or Partially Used. Revert to Open or
                    Draft is blocked if invoices were applied or refunds exist.
                  </>
                ),
              },
              {
                term: "Cancelled",
                desc: (
                  <>
                    Unapplies mapped invoices, puts Current Balance back, and
                    for Goods Return removes the returned stock. Blocked while
                    refunds exist. A cancelled Goods Return cannot be brought
                    back to Draft or Open.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Mark Credit Note As on a Draft offering Open and Cancelled." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Apply to Invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            On the list row and the detail when the credit note is{" "}
            <B>Open</B> or <B>Partially Used</B>. Modal title:{" "}
            <B>Apply Credits to Invoices</B>. This uses this credit
            note&apos;s available balance.
          </p>
          <Definitions
            items={[
              {
                term: "Which invoices",
                desc: (
                  <>
                    Same customer, still a balance, <B>Sent</B>,{" "}
                    <B>Partially Paid</B>, or <B>Overdue</B>.{" "}
                    <B>Show current branch documents only</B> limits the list
                    to this credit note&apos;s branch. Cross-currency apply is
                    blocked.
                  </>
                ),
              },
              {
                term: "What happens on save",
                desc: (
                  <>
                    Available balance goes down. The invoice moves toward
                    Partially Paid or Paid. Credit Applied on this detail lists
                    those rows — you can remove a row to unapply. When
                    available balance hits zero the credit note becomes{" "}
                    <B>Used</B>; if some remains it is <B>Partially Used</B>.
                  </>
                ),
              },
              {
                term: "Not invoice-header Apply Credit",
                desc: (
                  <>
                    That button on an invoice is opening-balance credit from
                    the customer branch. Do not use this page to document that
                    path — it lives on Invoices and Customer.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Apply Credits to Invoices modal with available credits, pending invoices, and Show current branch documents only." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Refund
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Same statuses as apply, and available balance must be greater than
            zero. Refund pays unused credit back instead of mapping it to an
            invoice.
          </p>
          <Definitions
            items={[
              {
                term: "What you enter",
                desc: (
                  <>
                    Refund amount (cannot exceed available balance), refund
                    date, and <B>From Account</B> — a cash / bank / payment
                    account. Reference and notes are optional.
                  </>
                ),
              },
              {
                term: "What happens",
                desc: (
                  <>
                    Available balance goes down. Used or Partially Used follows
                    the remaining amount. Refund History on the detail lists
                    the rows. You can delete a refund; that amount comes back.
                  </>
                ),
              },
              {
                term: "Cancel",
                desc: (
                  <>
                    Blocked while any refund remains. Delete the refunds first.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Refund on an Open credit note, From Account, and Refund History on the detail." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Deleting a credit note
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Delete is offered only when nothing has been applied to invoices
            and there are no refunds.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked when",
                desc: (
                  <>
                    Any invoice application, any refund, or status Used /
                    Closed / Partially Used.
                  </>
                ),
              },
              {
                term: "If it is not already Cancelled",
                desc: (
                  <>
                    Delete unapplies mapped invoices (if any slipped through),
                    puts Current Balance back, and for Goods Return removes
                    returned stock. The ledger journal is removed.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Delete disabled because this credit note was already applied to an invoice." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The invoice this note names, and where apply lands as paid down
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/customer-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Customer <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Unused credit on the list is this module&apos;s available balance
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/payments-received"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Payments Received <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The other way an invoice becomes Partially Paid or Paid
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
